import { getTemplateAssetRefs } from "./templateAssetRegistry";

const ROYAL_WEDDING_ASSET_FAMILY_ID = "royal_wedding_mobile_video_v1";

function buildBaseShapeAttrs({ id, type, name, x, y, width, height, opacity = 1, rotation = 0 }) {
  return {
    id,
    type,
    name,
    opacity,
    custom: {},
    visible: true,
    selectable: true,
    removable: true,
    alwaysOnTop: false,
    showInExport: true,
    x,
    y,
    width,
    height,
    rotation,
    animations: [],
    blurEnabled: false,
    blurRadius: 10,
    brightnessEnabled: false,
    brightness: 0,
    sepiaEnabled: false,
    grayscaleEnabled: false,
    filters: {},
    shadowEnabled: false,
    shadowBlur: 5,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowColor: "black",
    shadowOpacity: 1,
    draggable: true,
    resizable: true,
    contentEditable: true,
    styleEditable: true,
  };
}

function buildTextNode({
  id,
  name,
  text,
  x,
  y,
  width,
  height,
  fontFamily,
  fontSize,
  fontWeight = "normal",
  fontStyle = "normal",
  fill,
  align = "center",
  lineHeight = 1.2,
  letterSpacing = 0,
}) {
  return {
    ...buildBaseShapeAttrs({ id, type: "text", name, x, y, width, height }),
    text,
    placeholder: "",
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    textDecoration: "",
    fill,
    align,
    verticalAlign: "top",
    strokeWidth: 0,
    stroke: "black",
    lineHeight,
    letterSpacing,
    backgroundEnabled: false,
    backgroundColor: "#7ED321",
    backgroundOpacity: 1,
    backgroundCornerRadius: 0.5,
    backgroundPadding: 0.5,
  };
}

function buildFigureNode({
  id,
  name,
  x,
  y,
  width,
  height,
  fill,
  stroke = "rgba(233, 214, 174, 1)",
  strokeWidth = 0,
  opacity = 1,
  subType = "rect",
}) {
  return {
    ...buildBaseShapeAttrs({ id, type: "figure", name, x, y, width, height, opacity }),
    subType,
    fill,
    dash: [],
    stroke,
    strokeWidth,
    cornerRadius: 0,
  };
}

function getTemplateAssetSrc(assetRefs, role) {
  return assetRefs[role]?.src || "";
}

function buildSvgNode({
  id,
  name,
  src,
  x,
  y,
  width,
  height,
  opacity = 1,
  rotation = 0,
}) {
  return {
    ...buildBaseShapeAttrs({ id, type: "svg", name, x, y, width, height, opacity, rotation }),
    src: src || "",
    maskSrc: "",
    cropX: 0,
    cropY: 0,
    cropWidth: 1,
    cropHeight: 1,
    keepRatio: true,
    stretchEnabled: false,
    flipX: false,
    flipY: false,
    borderColor: "rgba(0,0,0,0)",
    borderSize: 0,
    cornerRadius: 0,
  };
}

function buildImageNode({
  id,
  name,
  src,
  x,
  y,
  width,
  height,
  opacity = 1,
  rotation = 0,
}) {
  return {
    ...buildBaseShapeAttrs({ id, type: "image", name, x, y, width, height, opacity, rotation }),
    src: src || "",
    cropX: 0,
    cropY: 0,
    cropWidth: 1,
    cropHeight: 1,
    keepRatio: false,
    flipX: false,
    flipY: false,
    clipSrc: "",
    borderColor: "rgba(0,0,0,0)",
    borderSize: 0,
    cornerRadius: 0,
  };
}

function getSelectedAssetByRole(selectedAssets, role) {
  return (selectedAssets || []).find(
    (asset) => asset?.role === role || asset?.asset_role === role
  );
}

function getSelectedAssetSrc(selectedAssets, role) {
  const asset = getSelectedAssetByRole(selectedAssets, role);
  return asset?.media_url || asset?.file_url || asset?.preview_url || asset?.thumbnail_url || "";
}

function isSvgAssetSrc(src, asset) {
  const assetType = String(asset?.asset_type || "").toLowerCase();
  return assetType.includes("svg") || String(src || "").toLowerCase().includes(".svg");
}

function buildSelectedAssetNode({
  selectedAssets,
  role,
  id,
  name,
  x,
  y,
  width,
  height,
  opacity = 1,
  rotation = 0,
}) {
  const asset = getSelectedAssetByRole(selectedAssets, role);
  const src = getSelectedAssetSrc(selectedAssets, role);

  if (!src) return null;

  const nodeProps = {
    id,
    name: name || `asset:${role}`,
    src,
    x,
    y,
    width,
    height,
    opacity,
    rotation,
  };

  return isSvgAssetSrc(src, asset) ? buildSvgNode(nodeProps) : buildImageNode(nodeProps);
}

function normalizeRoleToken(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
}

function buildPolotnoAnimationFromMotion(motion, fallbackDelay = 0) {
  const preset = String(motion?.preset || "none");
  if (preset === "none") return null;

  const delay = Math.round(Number(motion?.start_time_seconds ?? fallbackDelay) * 1000);
  const duration = Math.max(300, Math.round(Number(motion?.duration_seconds || 0.8) * 1000));
  const base = {
    delay,
    duration,
    enabled: true,
    type: "enter",
    name: "fade",
    data: {},
  };

  switch (preset) {
    case "fade_in":
      return base;
    case "fade_up":
      return { ...base, name: "move", data: { direction: "up" } };
    case "scale_in":
      return { ...base, name: "zoom", data: { direction: "in", strength: 0.45 } };
    case "slide_from_top":
      return { ...base, name: "move", data: { direction: "down" } };
    case "slide_from_bottom":
      return { ...base, name: "move", data: { direction: "up" } };
    case "slide_from_left":
    case "move_from_left_to_target":
      return { ...base, name: "move", data: { direction: "right" } };
    case "slide_from_right":
    case "move_from_right_to_target":
      return { ...base, name: "move", data: { direction: "left" } };
    case "soft_pulse":
      return {
        ...base,
        duration: Math.max(duration, 1800),
        type: "loop",
        name: "zoom",
        data: { direction: "in", strength: 0.08 },
      };
    case "slow_drift":
      return {
        ...base,
        duration: Math.max(duration, 3600),
        type: "loop",
        name: "move",
        data: { direction: "up" },
      };
    case "gentle_rotate":
      return {
        ...base,
        duration: Math.max(duration, 3000),
        type: "loop",
        name: "rotate",
        data: {},
      };
    case "staggered_text_reveal":
      return base;
    default:
      return base;
  }
}

function nodeMatchesMotionTarget(node, targetRole) {
  const target = normalizeRoleToken(targetRole);
  if (!target) return false;

  const nodeName = normalizeRoleToken(node?.name);
  const nodeId = normalizeRoleToken(node?.id);
  if (nodeName.includes(target) || nodeId.includes(target)) return true;

  return target.includes("text") && node?.type === "text";
}

function applyV2BriefToDraft(draft, v2Brief) {
  if (!v2Brief || !Array.isArray(v2Brief.scene_briefs)) return draft;

  const nextDraft = {
    ...draft,
    custom: {
      ...(draft.custom || {}),
      admin_template_v2_brief: v2Brief,
      generation_target: "admin_ai_template_v2",
    },
    pages: (draft.pages || []).map((page, pageIndex) => {
      const sceneBrief = v2Brief.scene_briefs[pageIndex];
      if (!sceneBrief) return page;

      const motions = Array.isArray(sceneBrief.element_motions)
        ? sceneBrief.element_motions
        : [];
      let textStaggerIndex = 0;

      return {
        ...page,
        name: sceneBrief.scene_name || page.name,
        duration: Math.round(Number(sceneBrief.duration_seconds || page.duration / 1000 || 5) * 1000),
        custom: {
          ...(page.custom || {}),
          v2_scene_brief: sceneBrief,
          transition_direction: sceneBrief.transition_direction || "",
          animation_direction: sceneBrief.animation_direction || "",
        },
        children: (page.children || []).map((child) => {
          const matchingMotion = motions.find((motion) =>
            nodeMatchesMotionTarget(child, motion?.target_role)
          );
          if (!matchingMotion) return child;

          const fallbackDelay =
            matchingMotion.preset === "staggered_text_reveal" && child.type === "text"
              ? 0.25 + textStaggerIndex++ * 0.2
              : 0;
          const animation = buildPolotnoAnimationFromMotion(matchingMotion, fallbackDelay);
          if (!animation) return child;

          return {
            ...child,
            animations: [animation, ...(child.animations || [])],
            custom: {
              ...(child.custom || {}),
              v2_motion: matchingMotion,
            },
          };
        }),
      };
    }),
  };

  return nextDraft;
}

function getPlacementBounds(placement, draft) {
  const width = Number(placement?.width) || draft?.width || 1080;
  const height = Number(placement?.height) || draft?.height || 1920;
  const slot = String(placement?.slot || "").toLowerCase();
  const mode = String(placement?.mode || "").toLowerCase();

  if (Number.isFinite(placement?.x) && Number.isFinite(placement?.y)) {
    return {
      x: placement.x,
      y: placement.y,
      width,
      height,
    };
  }

  if (mode === "cover" || slot.includes("background") || slot.includes("overlay") || slot.includes("frame")) {
    return {
      x: 0,
      y: 0,
      width: draft?.width || 1080,
      height: draft?.height || 1920,
    };
  }

  if (slot.includes("divider")) {
    return {
      x: 270,
      y: Number(placement?.y) || 820,
      width: 540,
      height: 88,
    };
  }

  if (slot.includes("icon")) {
    return {
      x: 455,
      y: Number(placement?.y) || 1180,
      width: 170,
      height: 170,
    };
  }

  if (slot.includes("center") || slot.includes("hero") || slot.includes("ornament")) {
    return {
      x: 300,
      y: Number(placement?.y) || 420,
      width: 480,
      height: 480,
    };
  }

  return {
    x: 180,
    y: 360,
    width: 720,
    height: 720,
  };
}

function getPlacementInsertIndex(children, slot) {
  const normalizedSlot = String(slot || "").toLowerCase();

  if (normalizedSlot.includes("background")) return 0;
  if (normalizedSlot.includes("overlay") || normalizedSlot.includes("frame")) {
    const firstTextIndex = children.findIndex((child) => child?.type === "text");
    return firstTextIndex === -1 ? children.length : firstTextIndex;
  }

  return children.length;
}

function applySelectedAssetPlacements(draft, selectedAssets = []) {
  if (!selectedAssets.length) return draft;

  const placeableAssets = selectedAssets.filter(
    (asset) => asset?.usage_mode === "place_directly" && Array.isArray(asset?.placement)
  );

  if (!placeableAssets.length) return draft;

  return {
    ...draft,
    pages: (draft.pages || []).map((page) => {
      const pagePlacements = [];

      placeableAssets.forEach((asset) => {
        (asset.placement || [])
          .filter((placement) => placement?.sceneId === page.id)
          .forEach((placement, index) => {
            const role = asset.role || asset.asset_role;
            const alreadyPlaced = (page.children || []).some((child) =>
              String(child?.name || "").includes(`asset:${role}`)
            );

            if (!role || alreadyPlaced) return;

            const bounds = getPlacementBounds(placement, draft);
            const node = buildSelectedAssetNode({
              selectedAssets,
              role,
              id: `asset-${page.id}-${role}-${index}`,
              name: `slot:${placement.slot || "template.asset"} asset:${role}`,
              x: bounds.x,
              y: bounds.y,
              width: bounds.width,
              height: bounds.height,
              opacity: placement.opacity ?? 1,
              rotation: placement.rotation || 0,
            });

            if (node) {
              pagePlacements.push({ node, slot: placement.slot });
            }
          });
      });

      if (!pagePlacements.length) return page;

      const nextChildren = [...(page.children || [])];
      pagePlacements.forEach(({ node, slot }) => {
        const insertIndex = getPlacementInsertIndex(nextChildren, slot);
        nextChildren.splice(insertIndex, 0, node);
      });

      return {
        ...page,
        children: nextChildren,
      };
    }),
  };
}

function resolveWeddingPalette(paletteText, plan) {
  const paletteSource = `${plan?.design_direction?.palette || ""} ${paletteText || ""}`.toLowerCase();
  const base = {
    heroBackground: "rgba(64, 16, 33, 1)",
    detailBackground: "rgba(82, 24, 42, 1)",
    accentBackground: "rgba(126, 31, 58, 1)",
    textPrimary: "rgba(245, 234, 214, 1)",
    textSecondary: "rgba(233, 214, 174, 1)",
    textMuted: "rgba(236, 224, 201, 1)",
  };

  if (paletteSource.includes("emerald") || paletteSource.includes("green")) {
    return {
      ...base,
      heroBackground: "rgba(12, 56, 45, 1)",
      detailBackground: "rgba(21, 75, 61, 1)",
      accentBackground: "rgba(183, 131, 45, 1)",
    };
  }

  if (paletteSource.includes("navy") || paletteSource.includes("blue")) {
    return {
      ...base,
      heroBackground: "rgba(16, 28, 61, 1)",
      detailBackground: "rgba(24, 43, 82, 1)",
      accentBackground: "rgba(195, 143, 52, 1)",
    };
  }

  if (paletteSource.includes("ivory") && !paletteSource.includes("maroon")) {
    return {
      ...base,
      heroBackground: "rgba(241, 229, 207, 1)",
      detailBackground: "rgba(232, 214, 181, 1)",
      accentBackground: "rgba(131, 34, 53, 1)",
      textPrimary: "rgba(71, 33, 42, 1)",
      textSecondary: "rgba(131, 86, 38, 1)",
      textMuted: "rgba(88, 61, 53, 1)",
    };
  }

  return base;
}

function scenePlanFor(plan, sceneId) {
  return (plan?.scene_plan || []).find((scene) => scene?.id === sceneId) || {};
}

function buildFrameNodes(prefix, colors) {
  return [
    buildFigureNode({
      id: `${prefix}-top-frame`,
      name: "decor:top_gold_frame",
      x: 72,
      y: 92,
      width: 936,
      height: 5,
      fill: colors.textSecondary,
      opacity: 0.9,
    }),
    buildFigureNode({
      id: `${prefix}-bottom-frame`,
      name: "decor:bottom_gold_frame",
      x: 72,
      y: 1818,
      width: 936,
      height: 5,
      fill: colors.textSecondary,
      opacity: 0.9,
    }),
    buildFigureNode({
      id: `${prefix}-left-frame`,
      name: "decor:left_gold_frame",
      x: 72,
      y: 92,
      width: 5,
      height: 1730,
      fill: colors.textSecondary,
      opacity: 0.45,
    }),
    buildFigureNode({
      id: `${prefix}-right-frame`,
      name: "decor:right_gold_frame",
      x: 1003,
      y: 92,
      width: 5,
      height: 1730,
      fill: colors.textSecondary,
      opacity: 0.45,
    }),
  ];
}

function buildSceneNoteNode({ id, scene, x = 140, y = 1660, width = 800, colors }) {
  const title = scene?.title ? `${scene.title}: ` : "";
  const goal = scene?.goal || "AI scene direction placeholder.";

  return buildTextNode({
    id,
    name: "slot:ai.scene_direction",
    text: `${title}${goal}`,
    x,
    y,
    width,
    height: 120,
    fontFamily: "Cormorant Garamond",
    fontSize: 24,
    fill: colors.textSecondary,
    lineHeight: 1.25,
    letterSpacing: 0.2,
  });
}

function buildAssetNoteNode({ id, plan, x = 140, y = 1500, width = 800, colors }) {
  const assets = Array.isArray(plan?.asset_requirements) ? plan.asset_requirements : [];
  const assetNames = assets
    .slice(0, 3)
    .map((asset) => asset?.id || asset?.description)
    .filter(Boolean)
    .join(", ");

  return buildTextNode({
    id,
    name: "slot:ai.asset_requirements",
    text: assetNames ? `Asset plan: ${assetNames}` : "Asset plan: royal arch frame, mandala, divider",
    x,
    y,
    width,
    height: 90,
    fontFamily: "Cormorant Garamond",
    fontSize: 22,
    fill: colors.textMuted,
    lineHeight: 1.25,
    letterSpacing: 0.2,
  });
}

export function buildRoyalWeddingDraft({
  templateName,
  paletteText,
  copyStyle,
  culturalContextText,
  plan,
}) {
  const copySlots = plan?.copy_slots || {};
  const slotText = (key, fallback) => copySlots[key] || fallback;
  const colors = resolveWeddingPalette(paletteText, plan);
  const {
    heroBackground,
    detailBackground,
    accentBackground,
    textPrimary,
    textSecondary,
    textMuted,
  } = colors;
  const royalAssets = getTemplateAssetRefs(ROYAL_WEDDING_ASSET_FAMILY_ID);
  const assetSrc = (role) => getTemplateAssetSrc(royalAssets, role);
  const sceneGrandOpening = scenePlanFor(plan, "scene-grand-opening");
  const sceneInvitation = scenePlanFor(plan, "scene-invitation-message");
  const sceneCeremony = scenePlanFor(plan, "scene-ceremony-details");
  const sceneVenue = scenePlanFor(plan, "scene-venue-address");
  const sceneClosing = scenePlanFor(plan, "scene-closing-rsvp");
  const designSummary = plan?.design_direction?.summary
    ? ` Direction: ${plan.design_direction.summary}`
    : "";
  const plannerNote =
    `Spec-driven draft for ${templateName}. Palette: ${paletteText}. ` +
    `Copy style: ${copyStyle}. Context: ${culturalContextText}.${designSummary}`;

  return {
    width: 1080,
    height: 1920,
    fonts: [],
    audios: [],
    custom: {},
    schemaVersion: 2,
    unit: "px",
    dpi: 72,
    pages: [
      {
        id: "scene-grand-opening",
        name: "Scene 1 - Grand Opening",
        width: "auto",
        height: "auto",
        background: heroBackground,
        bleed: 0,
        custom: {},
        duration: 5000,
        children: [
          ...buildFrameNodes("grand-opening", colors),
          buildSvgNode({
            id: "asset-grand-opening-royal-arch",
            name: "asset:royal_arch_frame",
            src: assetSrc("royal_arch_frame"),
            x: 140,
            y: 130,
            width: 800,
            height: 1088,
            opacity: 0.4,
          }),
          buildSvgNode({
            id: "asset-grand-opening-corner-left",
            name: "asset:paisley_corner_left",
            src: assetSrc("paisley_corner"),
            x: 62,
            y: 96,
            width: 220,
            height: 220,
            opacity: 0.75,
          }),
          buildSvgNode({
            id: "asset-grand-opening-corner-right",
            name: "asset:paisley_corner_right",
            src: assetSrc("paisley_corner"),
            x: 798,
            y: 96,
            width: 220,
            height: 220,
            opacity: 0.75,
            rotation: 90,
          }),
          buildFigureNode({
            id: "node-grand-opening-mandala-placeholder",
            name: "decor:ai_mandala_centerpiece",
            x: 230,
            y: 1060,
            width: 620,
            height: 620,
            fill: accentBackground,
            stroke: textSecondary,
            strokeWidth: 8,
            opacity: 0.28,
          }),
          buildSvgNode({
            id: "asset-grand-opening-mandala",
            name: "asset:mandala_centerpiece",
            src: assetSrc("mandala_centerpiece"),
            x: 250,
            y: 1088,
            width: 580,
            height: 580,
            opacity: 0.62,
          }),
          buildSvgNode({
            id: "asset-grand-opening-divider",
            name: "asset:ornamental_divider",
            src: assetSrc("ornamental_divider"),
            x: 250,
            y: 1010,
            width: 580,
            height: 72,
            opacity: 0.9,
          }),
          buildTextNode({
            id: "node-grand-opening-kicker",
            name: "slot:text.family_line",
            text: slotText("family_line", "Together with their families"),
            x: 150,
            y: 220,
            width: 780,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 30,
            fontWeight: "bold",
            fill: textSecondary,
            lineHeight: 1.3,
            letterSpacing: 1.2,
          }),
          buildTextNode({
            id: "node-grand-opening-planner-note",
            name: "slot:text.planner_note",
            text: plannerNote,
            x: 120,
            y: 320,
            width: 840,
            height: 120,
            fontFamily: "Cormorant Garamond",
            fontSize: 22,
            fill: "rgba(205, 180, 123, 1)",
            lineHeight: 1.3,
            letterSpacing: 0.3,
          }),
          buildTextNode({
            id: "node-grand-opening-bride-name",
            name: "slot:text.bride_name",
            text: slotText("bride_name", "Bride Name"),
            x: 120,
            y: 560,
            width: 840,
            height: 150,
            fontFamily: "Playfair Display",
            fontSize: 112,
            fontWeight: "bold",
            fill: textPrimary,
            lineHeight: 1.08,
          }),
          buildTextNode({
            id: "node-grand-opening-ampersand",
            name: "slot:text.ampersand",
            text: "&",
            x: 470,
            y: 720,
            width: 140,
            height: 90,
            fontFamily: "Playfair Display",
            fontSize: 54,
            fontWeight: "bold",
            fontStyle: "italic",
            fill: textSecondary,
            lineHeight: 1.1,
            letterSpacing: 1,
          }),
          buildTextNode({
            id: "node-grand-opening-groom-name",
            name: "slot:text.groom_name",
            text: slotText("groom_name", "Groom Name"),
            x: 120,
            y: 840,
            width: 840,
            height: 150,
            fontFamily: "Playfair Display",
            fontSize: 112,
            fontWeight: "bold",
            fill: textPrimary,
            lineHeight: 1.08,
          }),
          buildTextNode({
            id: "node-grand-opening-asset-note",
            name: "slot:text.asset_note",
            text: "Hero background, mandala centerpiece, and divider asset slots resolve later",
            x: 140,
            y: 1440,
            width: 800,
            height: 100,
            fontFamily: "Cormorant Garamond",
            fontSize: 20,
            fill: "rgba(210, 187, 145, 1)",
            lineHeight: 1.25,
            letterSpacing: 0.2,
          }),
          buildAssetNoteNode({
            id: "node-grand-opening-ai-asset-note",
            plan,
            x: 140,
            y: 1540,
            colors,
          }),
          buildSceneNoteNode({
            id: "node-grand-opening-ai-scene-note",
            scene: sceneGrandOpening,
            colors,
          }),
        ],
      },
      {
        id: "scene-invitation-message",
        name: "Scene 2 - Invitation Message",
        width: "auto",
        height: "auto",
        background: heroBackground,
        bleed: 0,
        custom: {},
        duration: 4000,
        children: [
          ...buildFrameNodes("invitation-message", colors),
          buildSvgNode({
            id: "asset-invitation-message-corner-left",
            name: "asset:paisley_corner_left",
            src: assetSrc("paisley_corner"),
            x: 70,
            y: 100,
            width: 190,
            height: 190,
            opacity: 0.62,
          }),
          buildSvgNode({
            id: "asset-invitation-message-corner-right",
            name: "asset:paisley_corner_right",
            src: assetSrc("paisley_corner"),
            x: 820,
            y: 100,
            width: 190,
            height: 190,
            opacity: 0.62,
            rotation: 90,
          }),
          buildFigureNode({
            id: "node-invitation-message-panel",
            name: "decor:ai_copy_panel",
            x: 112,
            y: 390,
            width: 856,
            height: 720,
            fill: detailBackground,
            stroke: textSecondary,
            strokeWidth: 4,
            opacity: 0.55,
          }),
          buildTextNode({
            id: "node-invitation-message-line-1",
            name: "slot:text.invitation_line_1",
            text: slotText("invitation_line_1", "Request the honor of your presence"),
            x: 150,
            y: 500,
            width: 780,
            height: 120,
            fontFamily: "Cormorant Garamond",
            fontSize: 58,
            fontWeight: "bold",
            fill: textPrimary,
            lineHeight: 1.15,
            letterSpacing: 1.2,
          }),
          buildSvgNode({
            id: "asset-invitation-message-divider",
            name: "asset:ornamental_divider",
            src: assetSrc("ornamental_divider"),
            x: 270,
            y: 675,
            width: 540,
            height: 68,
            opacity: 0.95,
          }),
          buildTextNode({
            id: "node-invitation-message-line-2",
            name: "slot:text.invitation_line_2",
            text: slotText("invitation_line_2", "At the celebration of their wedding"),
            x: 150,
            y: 850,
            width: 780,
            height: 120,
            fontFamily: "Cormorant Garamond",
            fontSize: 40,
            fontWeight: "normal",
            fill: textMuted,
            lineHeight: 1.25,
            letterSpacing: 0.6,
          }),
          buildSceneNoteNode({
            id: "node-invitation-message-ai-scene-note",
            scene: sceneInvitation,
            colors,
          }),
        ],
      },
      {
        id: "scene-ceremony-details",
        name: "Scene 3 - Ceremony Details",
        width: "auto",
        height: "auto",
        background: detailBackground,
        bleed: 0,
        custom: {},
        duration: 5000,
        children: [
          ...buildFrameNodes("ceremony-details", colors),
          buildSvgNode({
            id: "asset-ceremony-details-mandala-watermark",
            name: "asset:mandala_watermark",
            src: assetSrc("mandala_centerpiece"),
            x: 274,
            y: 1120,
            width: 532,
            height: 532,
            opacity: 0.18,
          }),
          buildFigureNode({
            id: "node-ceremony-details-date-card",
            name: "decor:ai_details_card",
            x: 118,
            y: 560,
            width: 844,
            height: 560,
            fill: heroBackground,
            stroke: textSecondary,
            strokeWidth: 3,
            opacity: 0.4,
          }),
          buildTextNode({
            id: "node-ceremony-details-title",
            name: "slot:text.section_title",
            text: slotText("ceremony_title", "Wedding Ceremony"),
            x: 180,
            y: 260,
            width: 720,
            height: 90,
            fontFamily: "Cormorant Garamond",
            fontSize: 60,
            fontWeight: "bold",
            fill: textPrimary,
            lineHeight: 1.15,
            letterSpacing: 1.5,
          }),
          buildSvgNode({
            id: "asset-ceremony-details-divider",
            name: "asset:ornamental_divider",
            src: assetSrc("ornamental_divider"),
            x: 300,
            y: 420,
            width: 480,
            height: 64,
            opacity: 0.95,
          }),
          buildTextNode({
            id: "node-ceremony-details-date",
            name: "slot:text.wedding_date",
            text: slotText("wedding_date", "Wedding Date"),
            x: 160,
            y: 630,
            width: 760,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fill: textMuted,
            lineHeight: 1.25,
            letterSpacing: 0.6,
          }),
          buildTextNode({
            id: "node-ceremony-details-time",
            name: "slot:text.wedding_time",
            text: slotText("wedding_time", "Wedding Time"),
            x: 160,
            y: 790,
            width: 760,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fill: textMuted,
            lineHeight: 1.25,
            letterSpacing: 0.6,
          }),
          buildTextNode({
            id: "node-ceremony-details-venue",
            name: "slot:text.venue_name",
            text: slotText("venue_name", "Venue Name"),
            x: 140,
            y: 950,
            width: 800,
            height: 90,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fill: textMuted,
            lineHeight: 1.25,
            letterSpacing: 0.6,
          }),
          buildSceneNoteNode({
            id: "node-ceremony-details-ai-scene-note",
            scene: sceneCeremony,
            colors,
          }),
        ],
      },
      {
        id: "scene-venue-address",
        name: "Scene 4 - Venue Address",
        width: "auto",
        height: "auto",
        background: detailBackground,
        bleed: 0,
        custom: {},
        duration: 5000,
        children: [
          ...buildFrameNodes("venue-address", colors),
          buildSvgNode({
            id: "asset-venue-address-arch",
            name: "asset:royal_arch_frame_watermark",
            src: assetSrc("royal_arch_frame"),
            x: 190,
            y: 170,
            width: 700,
            height: 952,
            opacity: 0.16,
          }),
          buildFigureNode({
            id: "node-venue-address-map-panel",
            name: "decor:ai_venue_panel",
            x: 118,
            y: 590,
            width: 844,
            height: 520,
            fill: heroBackground,
            stroke: textSecondary,
            strokeWidth: 3,
            opacity: 0.36,
          }),
          buildTextNode({
            id: "node-venue-address-title",
            name: "slot:text.section_title",
            text: slotText("venue_title", "Venue"),
            x: 180,
            y: 280,
            width: 720,
            height: 90,
            fontFamily: "Cormorant Garamond",
            fontSize: 60,
            fontWeight: "bold",
            fill: textPrimary,
            lineHeight: 1.15,
            letterSpacing: 1.5,
          }),
          buildSvgNode({
            id: "asset-venue-address-divider",
            name: "asset:ornamental_divider",
            src: assetSrc("ornamental_divider"),
            x: 300,
            y: 440,
            width: 480,
            height: 64,
            opacity: 0.95,
          }),
          buildTextNode({
            id: "node-venue-address-full",
            name: "slot:text.full_address",
            text: slotText("full_address", "Full Address"),
            x: 120,
            y: 660,
            width: 840,
            height: 180,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fill: textMuted,
            lineHeight: 1.25,
            letterSpacing: 0.6,
          }),
          buildTextNode({
            id: "node-venue-address-city-state",
            name: "slot:text.city_state",
            text: slotText("city_state", "City, State"),
            x: 220,
            y: 940,
            width: 640,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 30,
            fill: textSecondary,
            lineHeight: 1.3,
            letterSpacing: 1.1,
          }),
          buildSceneNoteNode({
            id: "node-venue-address-ai-scene-note",
            scene: sceneVenue,
            colors,
          }),
        ],
      },
      {
        id: "scene-closing-rsvp",
        name: "Scene 5 - Closing and RSVP",
        width: "auto",
        height: "auto",
        background: detailBackground,
        bleed: 0,
        custom: {},
        duration: 5000,
        children: [
          ...buildFrameNodes("closing-rsvp", colors),
          buildSvgNode({
            id: "asset-closing-rsvp-sparkle",
            name: "asset:sparkle_overlay",
            src: assetSrc("sparkle_overlay"),
            x: 280,
            y: 240,
            width: 520,
            height: 520,
            opacity: 0.72,
          }),
          buildSvgNode({
            id: "asset-closing-rsvp-mandala",
            name: "asset:mandala_footer",
            src: assetSrc("mandala_centerpiece"),
            x: 320,
            y: 1190,
            width: 440,
            height: 440,
            opacity: 0.34,
          }),
          buildFigureNode({
            id: "node-closing-rsvp-glow",
            name: "decor:ai_sparkle_overlay_area",
            x: 170,
            y: 300,
            width: 740,
            height: 880,
            fill: accentBackground,
            stroke: textSecondary,
            strokeWidth: 5,
            opacity: 0.24,
          }),
          buildTextNode({
            id: "node-closing-rsvp-overlay-note",
            name: "slot:text.overlay_note",
            text: "Sparkle overlay slot",
            x: 360,
            y: 220,
            width: 360,
            height: 60,
            fontFamily: "Cormorant Garamond",
            fontSize: 22,
            fill: textSecondary,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "node-closing-rsvp-blessing",
            name: "slot:text.blessing_line",
            text: slotText("blessing_line", "With blessings from both families"),
            x: 140,
            y: 430,
            width: 800,
            height: 120,
            fontFamily: "Cormorant Garamond",
            fontSize: 56,
            fontWeight: "bold",
            fill: textPrimary,
            lineHeight: 1.15,
            letterSpacing: 1.2,
          }),
          buildSvgNode({
            id: "asset-closing-rsvp-divider",
            name: "asset:ornamental_divider",
            src: assetSrc("ornamental_divider"),
            x: 300,
            y: 610,
            width: 480,
            height: 64,
            opacity: 0.95,
          }),
          buildTextNode({
            id: "node-closing-rsvp-label",
            name: "slot:text.rsvp_label",
            text: slotText("rsvp_label", "RSVP"),
            x: 260,
            y: 810,
            width: 560,
            height: 90,
            fontFamily: "Playfair Display",
            fontSize: 54,
            fontWeight: "bold",
            fontStyle: "italic",
            fill: textSecondary,
            lineHeight: 1.1,
            letterSpacing: 1,
          }),
          buildTextNode({
            id: "node-closing-rsvp-contact",
            name: "slot:text.rsvp_contact",
            text: slotText("rsvp_contact", "RSVP Contact"),
            x: 180,
            y: 980,
            width: 720,
            height: 90,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fill: textMuted,
            lineHeight: 1.25,
            letterSpacing: 0.6,
          }),
          buildSceneNoteNode({
            id: "node-closing-rsvp-ai-scene-note",
            scene: sceneClosing,
            colors,
          }),
        ],
      },
    ],
  };
}

function resolveBirthdayPalette(paletteText, plan) {
  const paletteSource = `${plan?.design_direction?.palette || ""} ${paletteText || ""}`.toLowerCase();
  const base = {
    background: "rgba(255, 247, 215, 1)",
    panel: "rgba(255, 255, 255, 0.78)",
    primary: "rgba(255, 190, 61, 1)",
    secondary: "rgba(255, 111, 145, 1)",
    accent: "rgba(50, 184, 255, 1)",
    text: "rgba(61, 37, 64, 1)",
    muted: "rgba(112, 78, 112, 1)",
  };

  if (paletteSource.includes("neon") || paletteSource.includes("midnight") || paletteSource.includes("dark")) {
    return {
      ...base,
      background: "rgba(13, 9, 34, 1)",
      panel: "rgba(30, 22, 66, 0.78)",
      primary: "rgba(124, 60, 255, 1)",
      secondary: "rgba(0, 224, 255, 1)",
      accent: "rgba(255, 244, 199, 1)",
      text: "rgba(255, 244, 199, 1)",
      muted: "rgba(194, 178, 255, 1)",
    };
  }

  if (paletteSource.includes("pastel") || paletteSource.includes("cake")) {
    return {
      ...base,
      background: "rgba(255, 250, 242, 1)",
      panel: "rgba(255, 255, 255, 0.86)",
      primary: "rgba(255, 214, 232, 1)",
      secondary: "rgba(184, 230, 255, 1)",
      accent: "rgba(247, 201, 72, 1)",
      text: "rgba(85, 51, 77, 1)",
      muted: "rgba(132, 86, 118, 1)",
    };
  }

  return base;
}

function buildBirthdayConfetti(prefix, colors) {
  return [
    buildFigureNode({
      id: `${prefix}-confetti-1`,
      name: "decor:confetti_dot",
      x: 128,
      y: 156,
      width: 54,
      height: 54,
      fill: colors.secondary,
      opacity: 0.88,
    }),
    buildFigureNode({
      id: `${prefix}-confetti-2`,
      name: "decor:confetti_dot",
      x: 860,
      y: 220,
      width: 42,
      height: 42,
      fill: colors.accent,
      opacity: 0.85,
    }),
    buildFigureNode({
      id: `${prefix}-confetti-3`,
      name: "decor:confetti_bar",
      x: 780,
      y: 1460,
      width: 120,
      height: 22,
      fill: colors.primary,
      opacity: 0.76,
      rotation: -18,
    }),
    buildFigureNode({
      id: `${prefix}-confetti-4`,
      name: "decor:confetti_bar",
      x: 150,
      y: 1380,
      width: 112,
      height: 20,
      fill: colors.accent,
      opacity: 0.72,
      rotation: 20,
    }),
  ];
}

function resolveGaneshPalette(paletteText, plan) {
  const paletteSource = `${plan?.design_direction?.palette || ""} ${paletteText || ""}`.toLowerCase();
  const base = {
    background: "rgba(74, 18, 8, 1)",
    detailBackground: "rgba(124, 45, 18, 1)",
    panel: "rgba(255, 247, 214, 0.9)",
    primary: "rgba(194, 65, 12, 1)",
    secondary: "rgba(245, 158, 11, 1)",
    accent: "rgba(212, 175, 55, 1)",
    text: "rgba(255, 247, 214, 1)",
    darkText: "rgba(61, 31, 15, 1)",
    muted: "rgba(255, 219, 153, 1)",
  };

  if (paletteSource.includes("emerald") || paletteSource.includes("green")) {
    return {
      ...base,
      background: "rgba(8, 47, 36, 1)",
      detailBackground: "rgba(15, 81, 50, 1)",
      primary: "rgba(15, 81, 50, 1)",
      secondary: "rgba(249, 115, 22, 1)",
      accent: "rgba(250, 204, 21, 1)",
      muted: "rgba(255, 239, 184, 1)",
    };
  }

  if (paletteSource.includes("ivory") || paletteSource.includes("cream")) {
    return {
      ...base,
      background: "rgba(255, 243, 207, 1)",
      detailBackground: "rgba(255, 232, 179, 1)",
      panel: "rgba(255, 255, 255, 0.9)",
      text: "rgba(61, 31, 15, 1)",
      muted: "rgba(117, 62, 24, 1)",
      darkText: "rgba(61, 31, 15, 1)",
    };
  }

  return base;
}

function buildGaneshCornerDecor(prefix, colors) {
  return [
    buildFigureNode({
      id: `${prefix}-top-garland`,
      name: "decor:marigold_garland",
      x: 96,
      y: 112,
      width: 888,
      height: 18,
      fill: colors.secondary,
      stroke: colors.accent,
      strokeWidth: 2,
      opacity: 0.9,
    }),
    buildFigureNode({
      id: `${prefix}-bottom-garland`,
      name: "decor:marigold_garland",
      x: 96,
      y: 1790,
      width: 888,
      height: 18,
      fill: colors.secondary,
      stroke: colors.accent,
      strokeWidth: 2,
      opacity: 0.76,
    }),
    buildFigureNode({
      id: `${prefix}-left-diya-glow`,
      name: "decor:diya_glow",
      x: 112,
      y: 1580,
      width: 120,
      height: 120,
      fill: colors.accent,
      opacity: 0.24,
    }),
    buildFigureNode({
      id: `${prefix}-right-diya-glow`,
      name: "decor:diya_glow",
      x: 848,
      y: 1580,
      width: 120,
      height: 120,
      fill: colors.accent,
      opacity: 0.24,
    }),
  ];
}

export function buildGaneshChaturthiDraft({
  paletteText,
  plan,
  selectedAssets = [],
}) {
  const copySlots = plan?.copy_slots || {};
  const slotText = (key, fallback) => copySlots[key] || fallback;
  const colors = resolveGaneshPalette(paletteText, plan);
  const ganeshAsset = (role, props) =>
    buildSelectedAssetNode({
      selectedAssets,
      role,
      ...props,
    });

  return {
    width: 1080,
    height: 1920,
    fonts: [],
    audios: [],
    custom: {},
    schemaVersion: 2,
    unit: "px",
    dpi: 72,
    pages: [
      {
        id: "scene-ganesh-opening",
        name: "Scene 1 - Auspicious Opening",
        width: "auto",
        height: "auto",
        background: colors.background,
        bleed: 0,
        custom: {},
        duration: 4800,
        children: [
          ...[
            ganeshAsset("ganesha_hero_background", {
              id: "asset-ganesh-opening-hero-background",
              name: "slot:background.hero asset:ganesha_hero_background",
              x: 0,
              y: 0,
              width: 1080,
              height: 1920,
              opacity: 1,
            }),
            ganeshAsset("diya_glow_overlay", {
              id: "asset-ganesh-opening-diya-glow",
              name: "slot:overlay.glow asset:diya_glow_overlay",
              x: 0,
              y: 0,
              width: 1080,
              height: 1920,
              opacity: 0.38,
            }),
            ganeshAsset("marigold_frame", {
              id: "asset-ganesh-opening-marigold-top",
              name: "slot:frame.top asset:marigold_frame",
              x: 90,
              y: 82,
              width: 900,
              height: 190,
              opacity: 0.88,
            }),
            ganeshAsset("marigold_frame", {
              id: "asset-ganesh-opening-marigold-bottom",
              name: "slot:frame.bottom asset:marigold_frame",
              x: 90,
              y: 1645,
              width: 900,
              height: 190,
              opacity: 0.72,
              rotation: 180,
            }),
            ganeshAsset("rangoli_floor_accent", {
              id: "asset-ganesh-opening-rangoli-floor",
              name: "slot:ornament.floor asset:rangoli_floor_accent",
              x: 240,
              y: 1460,
              width: 600,
              height: 260,
              opacity: 0.76,
            }),
          ].filter(Boolean),
          ...buildGaneshCornerDecor("ganesh-opening", colors),
          buildFigureNode({
            id: "ganesh-opening-hero-halo",
            name: "decor:ganesha_halo_placeholder",
            x: 225,
            y: 350,
            width: 630,
            height: 630,
            fill: colors.accent,
            stroke: colors.secondary,
            strokeWidth: 10,
            opacity: 0.22,
          }),
          ganeshAsset("ganesha_icon_or_silhouette", {
            id: "asset-ganesh-opening-icon",
            name: "slot:ornament.centerpiece asset:ganesha_icon_or_silhouette",
            x: 315,
            y: 430,
            width: 450,
            height: 450,
            opacity: 0.92,
          }) ||
            buildFigureNode({
              id: "ganesh-opening-icon-placeholder",
              name: "asset:ganesha_icon_or_silhouette",
              x: 335,
              y: 455,
              width: 410,
              height: 410,
              fill: "rgba(255, 247, 214, 0.22)",
              stroke: colors.accent,
              strokeWidth: 8,
              opacity: 0.92,
            }),
          buildTextNode({
            id: "ganesh-opening-kicker",
            name: "slot:text.devotional_kicker",
            text: slotText("devotional_kicker", "Shree Ganeshaya Namah"),
            x: 128,
            y: 210,
            width: 824,
            height: 72,
            fontFamily: "Cormorant Garamond",
            fontSize: 38,
            fontWeight: "bold",
            fill: colors.muted,
            lineHeight: 1.2,
            letterSpacing: 1.1,
          }),
          buildTextNode({
            id: "ganesh-opening-invitation",
            name: "slot:text.invitation_line",
            text: slotText("invitation_line", "You are warmly invited"),
            x: 140,
            y: 1040,
            width: 800,
            height: 78,
            fontFamily: "Cormorant Garamond",
            fontSize: 44,
            fontWeight: "bold",
            fill: colors.muted,
            lineHeight: 1.2,
            letterSpacing: 0.8,
          }),
          buildTextNode({
            id: "ganesh-opening-title",
            name: "slot:text.festival_title",
            text: slotText("festival_title", "Ganesh Chaturthi Celebration"),
            x: 104,
            y: 1155,
            width: 872,
            height: 190,
            fontFamily: "Playfair Display",
            fontSize: 82,
            fontWeight: "bold",
            fill: colors.text,
            lineHeight: 1.08,
          }),
          buildTextNode({
            id: "ganesh-opening-blessing",
            name: "slot:text.opening_blessing",
            text: slotText("opening_blessing", "Ganpati Bappa Morya"),
            x: 145,
            y: 1450,
            width: 790,
            height: 90,
            fontFamily: "Cormorant Garamond",
            fontSize: 46,
            fontWeight: "bold",
            fill: colors.muted,
            lineHeight: 1.15,
          }),
        ],
      },
      {
        id: "scene-ganesh-host",
        name: "Scene 2 - Host and Occasion",
        width: "auto",
        height: "auto",
        background: colors.detailBackground,
        bleed: 0,
        custom: {},
        duration: 4400,
        children: [
          ...buildGaneshCornerDecor("ganesh-host", colors),
          ...[
            ganeshAsset("ganesha_icon_or_silhouette", {
              id: "asset-ganesh-host-watermark",
              name: "slot:watermark.deity asset:ganesha_icon_or_silhouette",
              x: 300,
              y: 205,
              width: 480,
              height: 480,
              opacity: 0.12,
            }),
            ganeshAsset("diya_glow_overlay", {
              id: "asset-ganesh-host-diya-glow",
              name: "slot:overlay.soft_glow asset:diya_glow_overlay",
              x: 0,
              y: 0,
              width: 1080,
              height: 1920,
              opacity: 0.12,
            }),
          ].filter(Boolean),
          buildFigureNode({
            id: "ganesh-host-panel",
            name: "decor:devotional_copy_panel",
            x: 112,
            y: 380,
            width: 856,
            height: 850,
            fill: colors.panel,
            stroke: colors.accent,
            strokeWidth: 6,
            opacity: 0.9,
          }),
          buildTextNode({
            id: "ganesh-host-label",
            name: "slot:text.hosted_by",
            text: "Hosted by",
            x: 170,
            y: 500,
            width: 740,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 38,
            fontWeight: "bold",
            fill: colors.primary,
            lineHeight: 1.2,
            letterSpacing: 1,
          }),
          buildTextNode({
            id: "ganesh-host-name",
            name: "slot:text.host_name",
            text: slotText("host_name", "Host / Family / Mandal Name"),
            x: 150,
            y: 640,
            width: 780,
            height: 150,
            fontFamily: "Playfair Display",
            fontSize: 64,
            fontWeight: "bold",
            fill: colors.darkText,
            lineHeight: 1.12,
          }),
          ganeshAsset("lotus_divider", {
            id: "asset-ganesh-host-lotus-divider",
            name: "slot:divider.host asset:lotus_divider",
            x: 270,
            y: 830,
            width: 540,
            height: 88,
            opacity: 0.9,
          }) ||
            buildFigureNode({
              id: "ganesh-host-divider",
              name: "decor:lotus_divider_placeholder",
              x: 300,
              y: 860,
              width: 480,
              height: 10,
              fill: colors.accent,
              opacity: 0.88,
            }),
          buildTextNode({
            id: "ganesh-host-note",
            name: "slot:text.occasion_note",
            text: slotText("occasion_note", "Join us for puja, aarti, prasad, and celebration"),
            x: 160,
            y: 980,
            width: 760,
            height: 150,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fill: colors.darkText,
            lineHeight: 1.22,
            letterSpacing: 0.2,
          }),
          buildTextNode({
            id: "ganesh-host-footer",
            name: "slot:text.host_footer",
            text: slotText("host_footer", "With devotion and joy"),
            x: 160,
            y: 1375,
            width: 760,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 34,
            fontWeight: "bold",
            fill: colors.muted,
            lineHeight: 1.2,
          }),
        ],
      },
      {
        id: "scene-ganesh-details",
        name: "Scene 3 - Puja and Program Details",
        width: "auto",
        height: "auto",
        background: colors.background,
        bleed: 0,
        custom: {},
        duration: 5000,
        children: [
          ...[
            ganeshAsset("ganesha_icon_or_silhouette", {
              id: "asset-ganesh-details-watermark",
              name: "slot:watermark.deity asset:ganesha_icon_or_silhouette",
              x: 315,
              y: 1035,
              width: 450,
              height: 450,
              opacity: 0.1,
            }),
            ganeshAsset("diya_glow_overlay", {
              id: "asset-ganesh-details-diya-glow",
              name: "slot:overlay.soft_glow asset:diya_glow_overlay",
              x: 0,
              y: 0,
              width: 1080,
              height: 1920,
              opacity: 0.14,
            }),
          ].filter(Boolean),
          ...buildGaneshCornerDecor("ganesh-details", colors),
          buildFigureNode({
            id: "ganesh-details-card",
            name: "decor:readable_details_card",
            x: 118,
            y: 315,
            width: 844,
            height: 1080,
            fill: colors.panel,
            stroke: colors.accent,
            strokeWidth: 6,
            opacity: 0.94,
          }),
          buildTextNode({
            id: "ganesh-details-title",
            name: "slot:text.details_title",
            text: "Puja & Program Details",
            x: 150,
            y: 430,
            width: 780,
            height: 92,
            fontFamily: "Playfair Display",
            fontSize: 56,
            fontWeight: "bold",
            fill: colors.darkText,
            lineHeight: 1.1,
          }),
          ...[
            ganeshAsset("lotus_divider", {
              id: "asset-ganesh-details-lotus-divider",
              name: "slot:divider.details asset:lotus_divider",
              x: 300,
              y: 535,
              width: 480,
              height: 78,
              opacity: 0.9,
            }),
          ].filter(Boolean),
          buildTextNode({
            id: "ganesh-details-date",
            name: "slot:text.event_date",
            text: slotText("event_date", "Event Date"),
            x: 165,
            y: 620,
            width: 750,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fontWeight: "bold",
            fill: colors.primary,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "ganesh-details-puja-time",
            name: "slot:text.puja_time",
            text: slotText("puja_time", "Aarti / Puja Time"),
            x: 165,
            y: 770,
            width: 750,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fontWeight: "bold",
            fill: colors.primary,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "ganesh-details-highlights",
            name: "slot:text.program_highlights",
            text: slotText("program_highlights", "Puja | Aarti | Prasad | Celebration"),
            x: 155,
            y: 925,
            width: 770,
            height: 120,
            fontFamily: "Cormorant Garamond",
            fontSize: 38,
            fill: colors.darkText,
            lineHeight: 1.22,
          }),
          buildTextNode({
            id: "ganesh-details-prasad",
            name: "slot:text.prasad_note",
            text: slotText("prasad_note", "Prasad will be served"),
            x: 165,
            y: 1130,
            width: 750,
            height: 70,
            fontFamily: "Cormorant Garamond",
            fontSize: 34,
            fill: colors.darkText,
            lineHeight: 1.2,
          }),
          ...[
            ganeshAsset("modak_icon", {
              id: "asset-ganesh-details-modak-icon",
              name: "slot:icon.prasad asset:modak_icon",
              x: 455,
              y: 1235,
              width: 170,
              height: 170,
              opacity: 0.9,
            }),
          ].filter(Boolean),
          buildTextNode({
            id: "ganesh-details-footer",
            name: "slot:text.details_footer",
            text: slotText("details_footer", "Aarti, prasad, and blessings await"),
            x: 145,
            y: 1455,
            width: 790,
            height: 78,
            fontFamily: "Cormorant Garamond",
            fontSize: 34,
            fontWeight: "bold",
            fill: colors.muted,
            lineHeight: 1.2,
          }),
        ],
      },
      {
        id: "scene-ganesh-rsvp",
        name: "Scene 4 - Venue and RSVP",
        width: "auto",
        height: "auto",
        background: colors.detailBackground,
        bleed: 0,
        custom: {},
        duration: 5000,
        children: [
          ...[
            ganeshAsset("diya_glow_overlay", {
              id: "asset-ganesh-rsvp-diya-glow",
              name: "slot:overlay.glow asset:diya_glow_overlay",
              x: 0,
              y: 0,
              width: 1080,
              height: 1920,
              opacity: 0.24,
            }),
            ganeshAsset("marigold_frame", {
              id: "asset-ganesh-rsvp-marigold-top",
              name: "slot:frame.top asset:marigold_frame",
              x: 90,
              y: 82,
              width: 900,
              height: 190,
              opacity: 0.78,
            }),
            ganeshAsset("marigold_frame", {
              id: "asset-ganesh-rsvp-marigold-bottom",
              name: "slot:frame.bottom asset:marigold_frame",
              x: 90,
              y: 1645,
              width: 900,
              height: 190,
              opacity: 0.62,
              rotation: 180,
            }),
            ganeshAsset("ganesha_icon_or_silhouette", {
              id: "asset-ganesh-rsvp-watermark",
              name: "slot:watermark.deity asset:ganesha_icon_or_silhouette",
              x: 330,
              y: 1080,
              width: 420,
              height: 420,
              opacity: 0.1,
            }),
            ganeshAsset("rangoli_floor_accent", {
              id: "asset-ganesh-rsvp-rangoli-floor",
              name: "slot:ornament.floor asset:rangoli_floor_accent",
              x: 260,
              y: 1390,
              width: 560,
              height: 240,
              opacity: 0.48,
            }),
          ].filter(Boolean),
          ...buildGaneshCornerDecor("ganesh-rsvp", colors),
          buildFigureNode({
            id: "ganesh-rsvp-card",
            name: "decor:venue_rsvp_card",
            x: 118,
            y: 315,
            width: 844,
            height: 1080,
            fill: colors.panel,
            stroke: colors.accent,
            strokeWidth: 6,
            opacity: 0.94,
          }),
          buildTextNode({
            id: "ganesh-rsvp-venue-title",
            name: "slot:text.venue_title",
            text: "Venue",
            x: 180,
            y: 430,
            width: 720,
            height: 86,
            fontFamily: "Playfair Display",
            fontSize: 60,
            fontWeight: "bold",
            fill: colors.darkText,
            lineHeight: 1.1,
          }),
          buildTextNode({
            id: "ganesh-rsvp-venue-name",
            name: "slot:text.venue_name",
            text: slotText("venue_name", "Venue Name"),
            x: 150,
            y: 600,
            width: 780,
            height: 92,
            fontFamily: "Cormorant Garamond",
            fontSize: 42,
            fontWeight: "bold",
            fill: colors.primary,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "ganesh-rsvp-address",
            name: "slot:text.venue_address",
            text: slotText("venue_address", "Venue Address"),
            x: 150,
            y: 735,
            width: 780,
            height: 150,
            fontFamily: "Cormorant Garamond",
            fontSize: 36,
            fill: colors.darkText,
            lineHeight: 1.22,
          }),
          buildTextNode({
            id: "ganesh-rsvp-contact",
            name: "slot:text.rsvp_contact",
            text: slotText("rsvp_contact", "RSVP / Contact"),
            x: 165,
            y: 960,
            width: 750,
            height: 80,
            fontFamily: "Cormorant Garamond",
            fontSize: 38,
            fontWeight: "bold",
            fill: colors.primary,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "ganesh-rsvp-blessing",
            name: "slot:text.closing_blessing",
            text: slotText("closing_blessing", "Ganpati Bappa Morya"),
            x: 140,
            y: 1185,
            width: 800,
            height: 110,
            fontFamily: "Playfair Display",
            fontSize: 58,
            fontWeight: "bold",
            fill: colors.darkText,
            lineHeight: 1.1,
          }),
        ],
      },
    ],
  };
}

export function buildBirthdayPartyDraft({
  templateName,
  paletteText,
  copyStyle,
  culturalContextText,
  plan,
}) {
  const copySlots = plan?.copy_slots || {};
  const slotText = (key, fallback) => copySlots[key] || fallback;
  const colors = resolveBirthdayPalette(paletteText, plan);
  const designSummary = plan?.design_direction?.summary
    ? ` Direction: ${plan.design_direction.summary}`
    : "";
  const plannerNote =
    `Short birthday starter for ${templateName}. Palette: ${paletteText}. ` +
    `Copy style: ${copyStyle}. Context: ${culturalContextText}.${designSummary}`;

  return {
    width: 1080,
    height: 1920,
    fonts: [],
    audios: [],
    custom: {},
    schemaVersion: 2,
    unit: "px",
    dpi: 72,
    pages: [
      {
        id: "scene-birthday-opening",
        name: "Scene 1 - Birthday Opening",
        width: "auto",
        height: "auto",
        background: colors.background,
        bleed: 0,
        custom: {},
        duration: 4200,
        children: [
          ...buildBirthdayConfetti("birthday-opening", colors),
          buildFigureNode({
            id: "birthday-opening-hero-circle",
            name: "decor:party_hero_shape",
            x: 170,
            y: 300,
            width: 740,
            height: 740,
            fill: colors.primary,
            stroke: colors.secondary,
            strokeWidth: 10,
            opacity: 0.9,
          }),
          buildFigureNode({
            id: "birthday-opening-accent-circle",
            name: "decor:party_accent_shape",
            x: 255,
            y: 390,
            width: 570,
            height: 570,
            fill: colors.panel,
            stroke: colors.accent,
            strokeWidth: 6,
            opacity: 0.95,
          }),
          buildTextNode({
            id: "birthday-opening-kicker",
            name: "slot:text.party_kicker",
            text: slotText("party_kicker", "You're invited to celebrate"),
            x: 150,
            y: 420,
            width: 780,
            height: 80,
            fontFamily: "Poppins",
            fontSize: 38,
            fontWeight: "bold",
            fill: colors.muted,
            lineHeight: 1.2,
            letterSpacing: 0.6,
          }),
          buildTextNode({
            id: "birthday-opening-celebrant",
            name: "slot:text.celebrant_name",
            text: slotText("celebrant_name", "Celebrant Name"),
            x: 118,
            y: 555,
            width: 844,
            height: 180,
            fontFamily: "Poppins",
            fontSize: 92,
            fontWeight: "bold",
            fill: colors.text,
            lineHeight: 1.05,
          }),
          buildTextNode({
            id: "birthday-opening-headline",
            name: "slot:text.birthday_headline",
            text: slotText("birthday_headline", "Birthday Bash"),
            x: 140,
            y: 785,
            width: 800,
            height: 110,
            fontFamily: "Poppins",
            fontSize: 54,
            fontWeight: "bold",
            fill: colors.secondary,
            lineHeight: 1.1,
            letterSpacing: 0.4,
          }),
          buildTextNode({
            id: "birthday-opening-planner-note",
            name: "slot:text.planner_note",
            text: plannerNote,
            x: 132,
            y: 1180,
            width: 816,
            height: 130,
            fontFamily: "Poppins",
            fontSize: 24,
            fill: colors.muted,
            lineHeight: 1.28,
          }),
        ],
      },
      {
        id: "scene-birthday-details",
        name: "Scene 2 - Party Details",
        width: "auto",
        height: "auto",
        background: colors.background,
        bleed: 0,
        custom: {},
        duration: 4200,
        children: [
          ...buildBirthdayConfetti("birthday-details", colors),
          buildFigureNode({
            id: "birthday-details-panel",
            name: "decor:party_details_card",
            x: 118,
            y: 360,
            width: 844,
            height: 960,
            fill: colors.panel,
            stroke: colors.primary,
            strokeWidth: 8,
            opacity: 0.95,
          }),
          buildTextNode({
            id: "birthday-details-title",
            name: "slot:text.section_title",
            text: slotText("details_title", "Party Details"),
            x: 160,
            y: 460,
            width: 760,
            height: 110,
            fontFamily: "Poppins",
            fontSize: 64,
            fontWeight: "bold",
            fill: colors.text,
            lineHeight: 1.1,
          }),
          buildTextNode({
            id: "birthday-details-date",
            name: "slot:text.party_date",
            text: slotText("party_date", "Party Date"),
            x: 170,
            y: 675,
            width: 740,
            height: 80,
            fontFamily: "Poppins",
            fontSize: 42,
            fontWeight: "bold",
            fill: colors.secondary,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "birthday-details-time",
            name: "slot:text.party_time",
            text: slotText("party_time", "Party Time"),
            x: 170,
            y: 830,
            width: 740,
            height: 80,
            fontFamily: "Poppins",
            fontSize: 42,
            fontWeight: "bold",
            fill: colors.accent,
            lineHeight: 1.2,
          }),
          buildTextNode({
            id: "birthday-details-venue",
            name: "slot:text.venue_name",
            text: slotText("venue_name", "Venue Name"),
            x: 170,
            y: 985,
            width: 740,
            height: 120,
            fontFamily: "Poppins",
            fontSize: 38,
            fill: colors.muted,
            lineHeight: 1.25,
          }),
        ],
      },
      {
        id: "scene-birthday-rsvp",
        name: "Scene 3 - RSVP",
        width: "auto",
        height: "auto",
        background: colors.background,
        bleed: 0,
        custom: {},
        duration: 3600,
        children: [
          ...buildBirthdayConfetti("birthday-rsvp", colors),
          buildFigureNode({
            id: "birthday-rsvp-cake-shape",
            name: "decor:cake_placeholder",
            x: 300,
            y: 360,
            width: 480,
            height: 360,
            fill: colors.primary,
            stroke: colors.secondary,
            strokeWidth: 8,
            opacity: 0.88,
          }),
          buildTextNode({
            id: "birthday-rsvp-message",
            name: "slot:text.rsvp_message",
            text: slotText("rsvp_message", "Come hungry, leave happy!"),
            x: 130,
            y: 830,
            width: 820,
            height: 150,
            fontFamily: "Poppins",
            fontSize: 58,
            fontWeight: "bold",
            fill: colors.text,
            lineHeight: 1.15,
          }),
          buildTextNode({
            id: "birthday-rsvp-label",
            name: "slot:text.rsvp_label",
            text: slotText("rsvp_label", "RSVP"),
            x: 260,
            y: 1080,
            width: 560,
            height: 90,
            fontFamily: "Poppins",
            fontSize: 62,
            fontWeight: "bold",
            fill: colors.secondary,
            lineHeight: 1.1,
          }),
          buildTextNode({
            id: "birthday-rsvp-contact",
            name: "slot:text.rsvp_contact",
            text: slotText("rsvp_contact", "RSVP Contact"),
            x: 160,
            y: 1235,
            width: 760,
            height: 90,
            fontFamily: "Poppins",
            fontSize: 38,
            fill: colors.muted,
            lineHeight: 1.2,
          }),
        ],
      },
    ],
  };
}

const TEMPLATE_DRAFT_BUILDERS = {
  birthday_party: buildBirthdayPartyDraft,
  ganesh_chaturthi: buildGaneshChaturthiDraft,
  royal_wedding: buildRoyalWeddingDraft,
};

export function buildTemplateDraft({ family, form, plan }) {
  const builderKey = family?.draftBuilder || "royal_wedding";
  const builder = TEMPLATE_DRAFT_BUILDERS[builderKey];

  if (!builder) {
    throw new Error(`No Polotno draft builder registered for "${builderKey}".`);
  }

  const draft = builder({
    templateName: form.template_name.trim(),
    paletteText: form.palette_text.trim() || family.defaultForm?.palette_text || "",
    copyStyle: form.copy_style.trim() || family.defaultCopyStyle || "",
    culturalContextText: form.cultural_context_text.trim() || family.defaultCultureText || "",
    selectedAssets: form.selected_assets || [],
    plan,
  });

  const draftWithSelectedAssets = applySelectedAssetPlacements(draft, form.selected_assets || []);

  return applyV2BriefToDraft(draftWithSelectedAssets, form.v2_brief);
}

